import { useMsal } from "@azure/msal-react";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { fetchAccessToken } from "@valenzinsights/access-management-component";
import { endpoints } from "@valenzinsights/app-config";
import { IconButton, PageNavigationPlugin, Tooltip } from "@valenzinsights/core-component";
import { DocumentViewer as DocumentViewerComponent, IOverrideToolbarProps } from "@valenzinsights/document-viewer-component";
import axios from "axios";
import { useEffect, useState } from "react";
import { getCitationUrl, getDocumentUrl } from "../../../apis";

export const LibraryDocumentViewer: React.FC<{
	jumpToPagePluginInstance?: PageNavigationPlugin;
}> = ({ jumpToPagePluginInstance }) => {
	const [docUrl, setDocUrl] = useState<string | null>("");
	const [currentDocumentName, setCurrentDocumentName] = useState<string>("");
	const [renderedDocumentName, setRenderedDocumentName] = useState<string>("");
	const [renderedPageNumber, setRenderedPageNumber] = useState<number>(0);
	const queryParams = new URLSearchParams(location.search);
	const documentId = queryParams.get("documentid");
	const initialDocumentName = queryParams.get("documentName");
	const [isDisabled, setIsDisabled] = useState(false);
	const docViewerStyle = {
		padding: "10px",
		paddingBottom: "24px",
		height: "calc(100vh - 15px)",
		scrollbarWidth: "thin" as "auto" | "thin" | "none",
	};

	const { instance } = useMsal();

	useEffect(() => {
		if (documentId) {
			getDocumentUrl(documentId).then(setDocUrl);
		} else if (initialDocumentName) {
			setCurrentDocumentName(initialDocumentName);
		}
	}, [documentId, initialDocumentName]);

	const fetchCitationUrl = async (documentName: string) => {
		const response = await fetchAccessToken(
			instance,
			instance?.getAllAccounts()[0],
			["User.Read"],
			false,
		);

		if (response) {
			const appTokenResponse = await axios.get(
				`${endpoints.backendApiUrl}/token`,
				{
					headers: {
						"Content-Type": "application/json",
						Authorization: "Bearer " + response.idToken,
					},
				},
			);
			if (appTokenResponse?.data) {
				localStorage.setItem("role_management", appTokenResponse.data.token);

				const url = await getCitationUrl(documentName);

				if (url) {
					setRenderedPageNumber(getPageNumber(currentDocumentName));
					setRenderedDocumentName(currentDocumentName);
					setDocUrl(url);
					setIsDisabled(false);
				}
				else{
					setIsDisabled(true);
				}
			}
		}
	};
	
	useEffect(() => {
		if (currentDocumentName) {
			fetchCitationUrl(currentDocumentName);
		}
	}, [currentDocumentName]);

	const getPageNumber = (name: string) => {
		const match = name.match(/-(\d+)\.pdf$/);
		return match ? parseInt(match[1], 10) : 0;
	};

	const getBaseName = (name: string) => {
		const match = name.match(/^(.*)-\d+\.pdf$/);
		return match ? match[1] : name.replace(".pdf", "");
	};

	const updateDocumentName = (pageNumber: number) => {
		const baseName = getBaseName(renderedDocumentName);
		setCurrentDocumentName(`${baseName}-${pageNumber}.pdf`);
	};

	const goToPreviousPage = () => {
		const currentPage = getPageNumber(renderedDocumentName);
		if (currentPage > 0) {
			updateDocumentName(currentPage - 1);
		}
	};

	const goToNextPage = () => {
		const currentPage = getPageNumber(renderedDocumentName);
		updateDocumentName(currentPage + 1);
	};

	const goToPreviousPageElement = () => (
		<Tooltip title="Previous Page">
			<div style={{ padding: "0px 2px" }}>
				<IconButton onClick={goToPreviousPage} disabled={renderedPageNumber == 1}>
					<KeyboardArrowLeftIcon />
				</IconButton>
			</div>
		</Tooltip>
	);

	const currentPageElement = () => (
		<div style={{ padding: "0px 2px", marginLeft: "2px", marginRight: "2px" }}>{renderedPageNumber}</div>
	);

	const goToNextPageElement = () => (
		<Tooltip title="Next Page">
			<div style={{ padding: "0px 2px"}}>
				<IconButton onClick={goToNextPage} disabled={isDisabled}>
					<KeyboardArrowRightIcon />
				</IconButton>
			</div>
		</Tooltip>
	);

	const overrideToolbar : IOverrideToolbarProps = {
		GoToNextPage: goToNextPageElement,
		GoToPreviousPage: goToPreviousPageElement,
		CurrentPageInput: currentPageElement,
	};

	return (
		docUrl && (
			<DocumentViewerComponent
				documentUrl={docUrl}
				fileType="pdf"
				enableDefaultLayout={true}
				style={docViewerStyle}
				plugins={jumpToPagePluginInstance ? [jumpToPagePluginInstance] : []}
				overrideToolbar={initialDocumentName ? overrideToolbar : undefined}
			/>
		)
	);
};
